import React from "react";
import { toast } from "react-toastify";
import {
  Layouts as GridLayouts,
  Layout
} from "react-grid-layout";
import {
  useFullScreenHandle,
  FullScreenHandle
} from "react-full-screen";
import _ from "lodash";

import { ICustomer } from "src/core/customers/api/customers.api";
import {
  DateRangeOptions,
  FilterTypeOptions,
  IFilterType,
  useDashboardApi,
  IDashboard,
  IWidget
} from "src/core/dashboard/api/dashboard.api";
import {
  mergeLayoutOptions,
  transformDashboardFilters
} from "src/core/dashboard/utils";
import { IDrop } from "src/core/drops/api/drops.api";
import { useCurrentUser } from "src/core/users/hooks";
import { IApiError } from "src/library/utils/errors/apiError";

import { mergeNewWidget } from "../utils/mergeLayoutOptions";

/**
 * The hard coded variable settings used for layouts
 */
export const dashboardLayoutConfig = {
  // Set row height for grid layout
  rowHeight: 30,
  // Margin between each item in the grid
  margin: 10,
  // Top level available column sizes
  cols: {
    lg: 4,
    md: 2,
    sm: 1
  }
};

export enum DashboardMode {
  View = "view",
  Edit = "edit"
}

// State shape
export interface DashboardGlobalFiltersState {
  // The date range filter type id, required for update filters API request
  dateRangeFilterTypeId?: number | null;
  // The selected dateRange option value
  dateRangeOptions?: DateRangeOptions | null;
  filterByType?: IFilterType | null;
  filterByValue?: FilterTypeOptions["customer"] | FilterTypeOptions["branch"] | null;
  // properties used for display purposes
  customer?: ICustomer | null;
  drop?: IDrop | null;
}

interface DashboardProviderProps {
  children: React.ReactNode;
}

interface DashboardContext {
  globalFilters: DashboardGlobalFiltersState;
  currentDashboard: IDashboard | null;
  filterTypes: IFilterType[];
  mode: DashboardMode;
  layoutsAndWidgets: {layouts: GridLayouts | null; widgets: IWidget[] };
  userEditedLayout: boolean;
  fullScreenState: FullScreenHandle;
  updateGlobalFilters: (data: DashboardGlobalFiltersState) => void;
  updateCurrentDashboard: (dashboard: IDashboard, updateType?: "local" | "api") => void;
  updateMode: (mode: DashboardMode) => void;
  addNewWidget: (widget: IWidget) => void;
  removeWidget: (widgetId: number | string) => void;
  updateCurrentLayout: (breakpoint: string, layouts: Layout[]) => void;
  updateUserEditedLayout: (hasChanged: boolean) => void;
  setContainerWidth: (width: number) => void;
}

// Initialise context
export const DashboardContext = React.createContext<DashboardContext | null>(null);

const initialGlobalFiltersState: DashboardGlobalFiltersState = {
  dateRangeFilterTypeId: null,
  dateRangeOptions: null,
  filterByType: null,
  filterByValue: null,
  customer: null,
  drop: null
};

// Create provider
export const DashboardProvider: React.FC<DashboardProviderProps> = ({ children }) => {
  const [ globalFilters, setGlobalFilters ] = React.useState<DashboardGlobalFiltersState>({});
  const [ currentDashboard, setCurrentDashboard ] = React.useState<IDashboard | null>(null);
  const [ getDefaultDashboardRes, getDefaultDashboardReq ] = useDashboardApi("GET_DEFAULT_DASHBOARD");
  const [ getDashboardRes, getDashboardReq ] = useDashboardApi("GET_DASHBOARD");
  const [ { currentUser } ] = useCurrentUser();
  const [ filterTypes, setFilterTypes ] = React.useState<IFilterType[]>([]);
  const [ getFilterTypesRes, getFilterTypesReq ] = useDashboardApi("GET_FILTER_TYPES");
  const [ mode, setMode ] = React.useState<DashboardMode>(DashboardMode.View);

  const [ layoutsAndWidgets, setLayoutsAndWidgets ] = React.useState<{layouts: GridLayouts | null; widgets: IWidget[] }>({
    layouts: null,
    widgets: []
  });

  const [ userEditedLayout, setUserEditedLayout ] = React.useState(false);
  const [ containerWidth, setContainerWidth ] = React.useState<number | null>(null);
  const fullScreenState = useFullScreenHandle();

  const updateGlobalFilters = React.useCallback((updatedState: DashboardGlobalFiltersState) => {
    setGlobalFilters(updatedState);
  }, []);

  const updateCurrentDashboard = React.useCallback((dashboard: IDashboard) => {
    // request dashboard
    getDashboardReq({ dashboardId: dashboard.id });
    // reset filters and layouts ready to be set on api response
    setGlobalFilters(initialGlobalFiltersState);

    setLayoutsAndWidgets({
      layouts: null,
      widgets: []
    });
  }, [ getDashboardReq ]);

  const updateUserEditedLayout = React.useCallback((hasChanged: boolean) => {
    setUserEditedLayout(hasChanged);
  }, []);

  const updateMode = React.useCallback((mode: DashboardMode) => {
    setMode(mode);
  }, []);

  // updates layouts state
  const updateCurrentLayout = React.useCallback((breakpoint: string, layout: Layout[]) => {
    setLayoutsAndWidgets(curr => ({
      widgets: curr.widgets,
      layouts: {
        ...curr.layouts,
        [ breakpoint ]: layout
      }
    }));
  }, []);

  // update a dashboards array of widgets by overwritting the entire array
  const addNewWidget = React.useCallback((widget: IWidget) => {
    setLayoutsAndWidgets(({ layouts, widgets }) => {
      const layoutState = mergeNewWidget({
        layouts: layouts ?? {},
        dashboardWidget: widget,
        containerWidth: containerWidth || 0
      });

      return {
        layouts: layoutState,
        widgets: [ ...widgets || [], widget ]
      };
    });
  }, [ containerWidth ]);

  // update a dashboards array of widgets by overwritting the entire array
  const removeWidget = React.useCallback((widgetToRemoveId: string | number) => {
    setLayoutsAndWidgets(({ layouts: currentLayout, widgets: currentWidgets }) => {
      const newLayout: GridLayouts = {
        lg: [],
        md: [],
        sm: []
      };

      // remove widget from dashboard layout object
      if (currentLayout) {
        for (const layoutGroup in currentLayout) {
          newLayout[ layoutGroup ] = _.filter(currentLayout[ layoutGroup ], layout => layout.i !== widgetToRemoveId.toString());
        }
      }

      return ({
        layouts: newLayout,
        widgets: _.filter(currentWidgets, widget => widget.id !== widgetToRemoveId)
      });
    });
  }, []);

  // request the default dashboard when loading for the first time
  React.useEffect(() => {
    if (!currentDashboard && currentUser?.id) {
      getDefaultDashboardReq({ userId: currentUser.id });
    }
  }, [
    currentDashboard,
    currentUser?.id,
    getDefaultDashboardReq
  ]);

  const handleDashboardResp = React.useCallback((dashboard: IDashboard | null, error: IApiError | null) => {
    if (error) {
      toast.error(error.message);
    }

    if (dashboard) {
      setCurrentDashboard(dashboard);

      if (containerWidth) {
      // merge user layout preferences and widget fixed configurations
        const mergedLayout = mergeLayoutOptions({
          dashboardLayout: dashboard.layout,
          dashboardWidgets: dashboard.widgets || [],
          containerWidth
        });

        setLayoutsAndWidgets(() => ({
          layouts: mergedLayout,
          widgets: dashboard.widgets || []
        }));
      }

      // update global filters with response filter preferences or reset if no filters provided
      if (dashboard.dashboardFilters) {
        setGlobalFilters(transformDashboardFilters(dashboard.dashboardFilters, filterTypes));
      } else {
        setGlobalFilters(initialGlobalFiltersState);
      }
    }
  }, [ containerWidth, filterTypes ]);

  // handle responses from getting default dashboard
  React.useEffect(() => {
    if (getDefaultDashboardRes.data || getDefaultDashboardRes.error) {
      handleDashboardResp(getDefaultDashboardRes.data, getDefaultDashboardRes.error);
      getDefaultDashboardRes.data = null;
      getDefaultDashboardRes.error = null;
    }
  }, [
    getDefaultDashboardRes,
    getDefaultDashboardRes?.data,
    getDefaultDashboardRes.error,
    handleDashboardResp
  ]);

  // handle responses from getting dashboard
  React.useEffect(() => {
    if (getDashboardRes?.data || getDashboardRes?.error) {
      handleDashboardResp(getDashboardRes.data, getDashboardRes.error);
      getDashboardRes.data = null;
      getDashboardRes.error = null;
    }
  }, [
    getDashboardRes,
    getDashboardRes?.data,
    getDashboardRes.error,
    handleDashboardResp
  ]);

  // get filterTypes to generate available options
  React.useEffect(() => {
    if (!getFilterTypesRes.data) {
      getFilterTypesReq(null);
    }
  }, [ getFilterTypesReq, getFilterTypesRes.data ]);

  React.useEffect(() => {
    // only need to update if filterTypes not set
    if (!filterTypes.length) {
      if (getFilterTypesRes?.error) {
        toast.error(getFilterTypesRes.error.message);
      }

      if (getFilterTypesRes.data) {
        setFilterTypes(getFilterTypesRes.data);

        // update global filters as well, if dashboard exists
        if (currentDashboard?.dashboardFilters) {
          setGlobalFilters(transformDashboardFilters(currentDashboard.dashboardFilters, getFilterTypesRes.data));
        }
      }
    }
  }, [
    currentDashboard?.dashboardFilters,
    filterTypes,
    getFilterTypesRes.data,
    getFilterTypesRes.error
  ]);

  return (
    <DashboardContext.Provider
      value={{
        globalFilters,
        currentDashboard,
        filterTypes,
        mode,
        layoutsAndWidgets,
        userEditedLayout,
        updateGlobalFilters,
        updateCurrentDashboard,
        updateMode,
        addNewWidget,
        removeWidget,
        updateCurrentLayout,
        updateUserEditedLayout,
        setContainerWidth,
        fullScreenState
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
};

export const useDashboardContext = () => {
  const currentUserContext = React.useContext(DashboardContext);

  if (!currentUserContext) {
    throw new Error(
      "useDashboardContext has to be used within <DashboardContext.Provider>"
    );
  }

  return currentUserContext;
};
